import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import Header from "../Header/header";
import Fotter from "../Fotter/fotter";
import {
  User,
  Package,
  Settings,
  LogOut,
  LayoutDashboard,
  Calendar as CalendarIcon,
  MapPin,
  Phone,
  Mail,
  ShieldCheck,
  ChevronRight,
  Clock,
  Trash2,
  Save,
  Eye,
  EyeOff,
  Globe,
  Lock,
  Key,
  MessageSquare,
  Star,
  Edit
} from "lucide-react";
import { toast } from "react-toastify";
import ConfirmModal from "../AdminDashboard/ConfirmModal";
import EditReviewModal from "../Reviews/EditReviewModal";
import API_URL from "../../config";


const SL_HOLIDAYS_2026 = [
  { date: "2026-01-01", name: "Duruthu Full Moon Poya Day" },
  { date: "2026-01-14", name: "Tamil Thai Pongal Day" },
  { date: "2026-02-04", name: "Independence Day" },
  { date: "2026-02-17", name: "Maha Shivaratri Day" },
  { date: "2026-04-13", name: "Sinhala & Tamil New Year Eve" },
  { date: "2026-04-14", name: "Sinhala & Tamil New Year Day" },
  { date: "2026-05-01", name: "May Day / Vesak Full Moon Poya Day" },
  { date: "2026-05-31", name: "Poson Full Moon Poya Day" },
  { date: "2026-12-25", name: "Christmas Day" },
];

const WorldClock = ({ city, timezone }) => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formattedTime = time.toLocaleTimeString("en-GB", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  return (
    <div className="bg-white p-4 rounded-2xl shadow-sm border border-sky-100 flex flex-col items-center justify-center space-y-1 transition-all hover:border-sky-300">
      <span className="text-sky-600 font-bold text-[10px] uppercase tracking-widest">{city}</span>
      <span className="text-xl font-black text-gray-800 font-mono tracking-tighter">{formattedTime}</span>
    </div>
  );
};

const WeatherWidget = () => {
  const [weatherData, setWeatherData] = useState(null);
  const [isLoadingWeather, setIsLoadingWeather] = useState(true);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        setIsLoadingWeather(true);
        const API_KEY = process.env.REACT_APP_WEATHER_API_KEY;
        const response = await fetch(
          `https://api.openweathermap.org/data/2.5/weather?q=Colombo,lk&units=metric&appid=${API_KEY}`
        );
        const data = await response.json();

        if (response.ok) {
          setWeatherData({
            temp: Math.round(data.main.temp),
            icon: `https://openweathermap.org/img/wn/${data.weather[0].icon}@2x.png`,
            description: data.weather[0].description,
            humidity: data.main.humidity
          });
        }
      } catch (error) {
        console.error("Failed to fetch weather data:", error);
      } finally {
        setIsLoadingWeather(false);
      }
    };

    fetchWeather();
    const intervalId = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="bg-gradient-to-br from-sky-400 to-sky-600 p-4 rounded-2xl shadow-sm border border-sky-400 flex flex-col items-center justify-center space-y-1 transition-all hover:border-sky-300 relative overflow-hidden group">
      <div className="absolute -right-8 -top-8 bg-white/10 w-24 h-24 rounded-full group-hover:scale-110 transition duration-500 z-0"></div>
      {isLoadingWeather ? (
        <div className="w-full h-full flex items-center justify-center z-10">
          <div className="w-8 h-8 rounded-full border-2 border-white/30 border-t-white animate-spin"></div>
        </div>
      ) : weatherData ? (
        <div className="flex flex-col items-center z-10 text-white w-full">
          <div className="flex items-center justify-between w-full px-2">
            <span className="font-bold text-[10px] uppercase tracking-widest opacity-90">Colombo</span>
            <span className="text-[10px] opacity-80 capitalize">{weatherData.description}</span>
          </div>
          <div className="flex items-center justify-center gap-1 mt-1">
            <img src={weatherData.icon} alt="Weather" className="w-10 h-10 drop-shadow-md" />
            <span className="text-2xl font-black tracking-tighter">{weatherData.temp}°C</span>
          </div>
        </div>
      ) : (
        <span className="text-white/80 text-sm z-10">Weather Unavailable</span>
      )}
    </div>
  );
};

const CustomCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const days = [];
  const startDay = firstDayOfMonth(month, year);
  const totalDays = daysInMonth(month, year);

  for (let i = 0; i < startDay; i++) days.push(null);
  for (let i = 1; i <= totalDays; i++) days.push(i);

  const isHoliday = (d) => {
    if (!d) return null;
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    return SL_HOLIDAYS_2026.find(h => h.date === dateStr);
  };

  const isToday = (d) => {
    const today = new Date();
    return d && d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  };

  return (
    <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-sky-900/5 border border-sky-100/50">
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-xl font-black text-sky-950 flex items-center gap-3">
          <div className="p-2 bg-sky-100 rounded-xl text-sky-600">
            <CalendarIcon size={20} />
          </div>
          {monthNames[month]} {year}
        </h3>
        <div className="flex gap-2">
          <button
            onClick={() => setCurrentDate(new Date(year, month - 1))}
            className="w-10 h-10 flex items-center justify-center bg-gray-50 hover:bg-sky-50 rounded-xl text-sky-600 transition-all border border-transparent hover:border-sky-100"
          >
            ◀
          </button>
          <button
            onClick={() => setCurrentDate(new Date(year, month + 1))}
            className="w-10 h-10 flex items-center justify-center bg-gray-50 hover:bg-sky-50 rounded-xl text-sky-600 transition-all border border-transparent hover:border-sky-100"
          >
            ▶
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-3 mb-4">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
          <div key={d} className="text-center text-[10px] font-black text-sky-900/30 uppercase tracking-widest">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-3">
        {days.map((day, i) => {
          const holiday = isHoliday(day);
          const activeToday = isToday(day);
          return (
            <div
              key={i}
              className={`
                h-12 md:h-16 rounded-2xl flex flex-col items-center justify-center relative group cursor-pointer transition-all duration-300
                ${!day ? "" : "hover:bg-sky-50 hover:scale-105 active:scale-95"}
                ${activeToday ? "bg-sky-500 text-white font-black shadow-lg shadow-sky-500/30 border-none z-10" : "bg-white text-sky-950 border border-transparent shadow-sm"}
                ${holiday && !activeToday ? "border-amber-200 bg-amber-50/50 text-amber-700" : ""}
              `}
            >
              <span className={`text-base ${day ? "font-bold" : "opacity-0"}`}>{day}</span>
              {holiday && (
                <>
                  <div className={`w-1.5 h-1.5 rounded-full mt-1 ${activeToday ? "bg-white" : "bg-amber-500"}`}></div>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 px-3 py-2 bg-sky-950 text-white text-[10px] font-bold rounded-xl opacity-0 group-hover:opacity-100 whitespace-nowrap z-50 transition-all pointer-events-none shadow-xl">
                    {holiday.name}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 pt-6 border-t border-sky-50 flex flex-wrap gap-6 text-[10px] font-black uppercase tracking-widest text-sky-900/40">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-sky-500 rounded-full shadow-lg shadow-sky-500/30"></div>
          <span>Today</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-amber-50 border border-amber-200 rounded-full"></div>
          <span>Holiday In LK</span>
        </div>
      </div>
    </div>
  );
};

function UserDashboard() {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [localTime, setLocalTime] = useState(new Date());
  const [userReviews, setUserReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [isReviewDeleteModalOpen, setIsReviewDeleteModalOpen] = useState(false);
  const [isReviewEditModalOpen, setIsReviewEditModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  // Profile Form State
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    currentPassword: "",
    newPassword: "",
    phone: user?.phone || "",
    address: user?.address || ""
  });

  useEffect(() => {
    const timer = setInterval(() => setLocalTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchUserBookings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/bookings/by-email/${user.email}`);
      setBookings(res.data.bookings || []);
    } catch (err) {
      console.error("Error fetching bookings:", err);
      toast.error("Failed to load your bookings");
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  const fetchUserReviews = useCallback(async () => {
    try {
      setLoadingReviews(true);
      const res = await axios.get(`${API_URL}/reviews/by-email/${user.email}`);
      setUserReviews(res.data.reviews || []);
    } catch (err) {
      console.error("Error fetching reviews:", err);
    } finally {
      setLoadingReviews(false);
    }
  }, [user?.email]);

  useEffect(() => {
    if (user?.email) {
      fetchUserBookings();
      fetchUserReviews();
    }
  }, [user?.email, fetchUserBookings, fetchUserReviews]);

  const handleDeleteClick = (review) => {
    setSelectedReview(review);
    setIsReviewDeleteModalOpen(true);
  };

  const confirmDeleteReview = async () => {
    if (!selectedReview) return;
    try {
      await axios.delete(`${API_URL}/reviews/${selectedReview._id}`);
      toast.success("Review deleted successfully");
      fetchUserReviews();
    } catch (err) {
      toast.error("Failed to delete review");
    } finally {
      setIsReviewDeleteModalOpen(false);
      setSelectedReview(null);
    }
  };

  const handleEditReviewClick = (review) => {
    setSelectedReview(review);
    setIsReviewEditModalOpen(true);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.put(`${API_URL}/user/update-profile/${user.id || user._id}`, form);
      toast.success(res.data.msg || "Profile updated successfully!");

      // Update local context to reflect name/phone/address changes
      updateUser({
        name: form.name,
        phone: form.phone,
        address: form.address
      });

      setForm({ ...form, currentPassword: "", newPassword: "" });
    } catch (err) {
      toast.error(err.response?.data?.msg || "Error updating profile");
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm("CRITICAL: Are you sure? This will permanently delete your account and all associated data.")) {
      setIsDeleting(true);
      try {
        await axios.delete(`${API_URL}/user/delete-account/${user.id || user._id}`);
        toast.info("Account deleted. We're sad to see you go.");
        logout();
        navigate("/");
      } catch (err) {
        toast.error("Failed to delete account");
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const StatCard = ({ icon: Icon, label, value, colorClass }) => (
    <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex items-center gap-5 transition-transform hover:scale-[1.02]">
      <div className={`p-4 rounded-2xl ${colorClass}`}>
        <Icon size={24} />
      </div>
      <div>
        <p className="text-gray-500 text-xs font-bold uppercase tracking-widest leading-none mb-1">{label}</p>
        <p className="text-2xl font-black text-gray-900 leading-none">{value}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <Header />

      <main className="flex-grow container mx-auto px-4 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

          {/* Sidebar / Navigation */}
          <aside className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-[2.5rem] shadow-xl shadow-sky-900/5 p-8 border border-sky-100/50 flex flex-col items-center text-center">
              <div className="w-24 h-24 bg-gradient-to-tr from-sky-400 to-blue-600 rounded-[2rem] flex items-center justify-center text-white mb-4 shadow-xl shadow-sky-500/30">
                <User size={48} />
              </div>
              <h2 className="text-xl font-black text-sky-950 truncate w-full">{user?.name || "Explorer"}</h2>
              <p className="text-sky-500 text-xs font-bold flex items-center gap-1 mt-1">
                <ShieldCheck size={12} /> Verified Member
              </p>

              <nav className="w-full mt-10 space-y-2">
                {[
                  { id: "overview", label: "Dashboard", icon: LayoutDashboard },
                  { id: "bookings", label: "My Journeys", icon: Package },
                  { id: "reviews", label: "My Reviews", icon: MessageSquare },
                  { id: "settings", label: "Account Settings", icon: Settings },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl transition-all group ${activeTab === item.id ? "bg-sky-500 text-white shadow-lg shadow-sky-500/20" : "text-gray-500 hover:bg-sky-50 hover:text-sky-600"}`}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon size={20} />
                      <span className="font-bold text-sm tracking-tight">{item.label}</span>
                    </div>
                    <ChevronRight size={16} className={`transition-transform duration-300 ${activeTab === item.id ? "rotate-90" : "group-hover:translate-x-1"}`} />
                  </button>
                ))}

                <button
                  onClick={() => setIsLogoutModalOpen(true)}
                  className="w-full flex items-center gap-3 px-5 py-4 rounded-2xl text-rose-500 hover:bg-rose-50 font-bold text-sm transition-colors mt-8"
                >
                  <LogOut size={20} />
                  <span>Log Out</span>
                </button>
              </nav>
            </div>

            {/* Sub Widgets in Sidebar - World Clocks */}
            {!isDeleting && activeTab === "overview" && (
              <div className="space-y-4 hidden lg:block animate-in fade-in slide-in-from-left-4 duration-700">
                <h4 className="text-[10px] font-black text-sky-900/30 uppercase tracking-[0.2em] px-4">World Clocks</h4>
                <div className="grid grid-cols-2 gap-3">
                  <WorldClock city="London" timezone="Europe/London" />
                  <WorldClock city="Tokyo" timezone="Asia/Tokyo" />
                  <WorldClock city="New York" timezone="America/New_York" />
                  <WorldClock city="Dubai" timezone="Asia/Dubai" />
                </div>
              </div>
            )}
          </aside>

          {/* Main Content Area */}
          <div className="lg:col-span-3">
            {activeTab === "overview" && (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div>
                    <h1 className="text-4xl font-black text-sky-950 tracking-tight">Bonjour, {user?.name?.split(' ')[0]}!</h1>
                    <p className="text-gray-500 font-medium mt-1">Ready for your next grand adventure?</p>
                  </div>
                </div>

                {/* Top Stats Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8 mt-6">
                  <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-6 rounded-2xl shadow-lg relative overflow-hidden group text-white">
                    <div className="relative z-10">
                      <h4 className="text-xs font-bold opacity-80 uppercase tracking-widest mb-1">Sri Lanka Time</h4>
                      <span className="text-2xl md:text-3xl font-black font-mono tracking-tighter">
                        {localTime.toLocaleTimeString("en-GB", { hour12: false })}
                      </span>
                      <p className="text-[10px] mt-2 opacity-70 flex items-center gap-1">
                        <MapPin size={12} /> Colombo, Sri Lanka
                      </p>
                    </div>
                    <div className="absolute right-0 bottom-0 opacity-10 translate-x-1/4 translate-y-1/4 group-hover:scale-110 transition-transform duration-500">
                      <Clock size={100} />
                    </div>
                  </div>
                  <WorldClock city="London" timezone="Europe/London" />
                  <WorldClock city="Tokyo" timezone="Asia/Tokyo" />
                  <WeatherWidget />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <StatCard
                    icon={Package}
                    label="Active Bookings"
                    value={bookings.filter(b => b.status === "Confirmed").length}
                    colorClass="bg-emerald-50 text-emerald-600"
                  />
                  <StatCard
                    icon={CalendarIcon}
                    label="Total Trips"
                    value={bookings.length}
                    colorClass="bg-sky-50 text-sky-600"
                  />
                  <StatCard
                    icon={LayoutDashboard}
                    label="Member Since"
                    value={new Date(user?.createdAt).getFullYear() || "2026"}
                    colorClass="bg-purple-50 text-purple-600"
                  />
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
                  {/* Calendar Widget */}
                  <div className="xl:col-span-3 order-2 xl:order-1">
                    <CustomCalendar />
                  </div>

                  {/* Next Trip & Quick Actions */}
                  <div className="xl:col-span-2 space-y-8 order-1 xl:order-2">
                    <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl shadow-sky-900/5 overflow-hidden h-full flex flex-col">
                      <div className="px-8 py-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
                        <h3 className="font-black text-sky-950 text-lg">Next Destination</h3>
                        <button onClick={() => setActiveTab("bookings")} className="text-sky-500 text-[10px] font-black uppercase tracking-widest hover:text-sky-600 transition">View All</button>
                      </div>
                      <div className="p-8 flex-1 flex flex-col justify-center">
                        {bookings.length > 0 ? (
                          <div className="space-y-6">
                            <div className="w-full h-48 bg-sky-100 rounded-[2rem] overflow-hidden shadow-inner group relative">
                              <img
                                src={bookings[0].accommodation?.image || "https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?q=80&w=1000"}
                                alt="trip"
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                              />
                              <div className="absolute top-4 right-4 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg">
                                <span className="text- sky-600 text-[10px] font-black uppercase tracking-widest">Active Stay</span>
                              </div>
                            </div>
                            <div className="space-y-3">
                              <h4 className="text-2xl font-black text-sky-950 leading-tight">
                                {bookings[0].accommodation?.accommodationName || "Upcoming Stay"}
                              </h4>
                              <div className="flex flex-wrap gap-4 text-xs font-bold text-gray-400">
                                <span className="flex items-center gap-1 bg-gray-50 px-3 py-1 rounded-full"><MapPin size={14} className="text-sky-500" /> Sri Lanka</span>
                                <span className="flex items-center gap-1 bg-gray-50 px-3 py-1 rounded-full"><CalendarIcon size={14} className="text-sky-500" /> {new Date(bookings[0].createdAt).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-10 space-y-6">
                            <div className="w-20 h-20 bg-sky-50 rounded-[2rem] flex items-center justify-center mx-auto text-sky-200">
                              <Globe size={40} />
                            </div>
                            <div className="space-y-2">
                              <h4 className="text-xl font-black text-sky-950">No Active Journeys</h4>
                              <p className="text-gray-400 text-sm font-medium px-4">Your passport is looking a bit empty. It's time to add some stamps!</p>
                            </div>
                            <button onClick={() => navigate('/destinationsuser')} className="px-8 py-4 bg-sky-500 text-white rounded-2xl font-black text-sm shadow-xl shadow-sky-500/30 hover:bg-sky-600 hover:scale-105 active:scale-95 transition-all">Start Exploring</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "bookings" && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
                <h2 className="text-3xl font-black text-sky-950 tracking-tight mb-8">My Journey Logs</h2>

                {loading ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : bookings.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {bookings.map((booking) => (
                      <div key={booking._id} className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-lg shadow-gray-200/40 flex flex-col justify-between group h-full transition-all hover:shadow-sky-200/50">
                        <div>
                          <div className="flex justify-between items-start mb-4">
                            <div className="w-12 h-12 bg-sky-50 rounded-2xl flex items-center justify-center text-sky-500 group-hover:bg-sky-500 group-hover:text-white transition-colors duration-300">
                              <MapPin size={24} />
                            </div>
                            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${booking.status === 'Confirmed' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                              {booking.status || "Confirmed"}
                            </span>
                          </div>
                          <h4 className="text-xl font-black text-sky-950 mb-2 truncate">{booking.accommodation?.accommodationName || "Stay Registration"}</h4>
                          <div className="space-y-2 mb-6">
                            <div className="flex items-center gap-2 text-xs text-gray-400 font-bold">
                              <CalendarIcon size={14} /> Created: {new Date(booking.createdAt).toLocaleDateString()}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-400 font-bold">
                              <User size={14} /> Quantity: {booking.qty} Adults
                            </div>
                          </div>
                        </div>
                        <div className="pt-4 border-t border-gray-50 flex items-center justify-between">
                          <div>
                            <p className="text-[10px] text-gray-400 uppercase font-black tracking-widest mb-0.5">Total Paid</p>
                            <p className="text-xl font-black text-sky-900 leading-none">LKR {booking.totalPrice?.toLocaleString()}</p>
                          </div>
                          <button onClick={() => navigate('/useraccommodations')} className="p-3 bg-sky-50 text-sky-500 rounded-2xl hover:bg-sky-500 hover:text-white transition-all shadow-sm">
                            <Eye size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-[2.5rem] p-12 text-center border-2 border-dashed border-sky-100">
                    <Package className="mx-auto text-sky-100 mb-6" size={80} />
                    <h3 className="text-2xl font-black text-sky-950">Empty Travels</h3>
                    <p className="text-gray-400 mt-2 font-medium max-w-xs mx-auto">Your booking list is looking a bit lonely. Start planning your getaway today.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "reviews" && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-3xl font-black text-sky-950 tracking-tight">My Review Portfolio</h2>
                  <button onClick={() => navigate('/viewreview')} className="px-5 py-2.5 bg-sky-500 text-white rounded-xl font-bold text-xs shadow-lg shadow-sky-500/20 hover:bg-sky-600 transition-all">Write New Review</button>
                </div>

                {loadingReviews ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : userReviews.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {userReviews.map((review) => (
                      <div key={review._id} className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-lg shadow-gray-200/40 flex flex-col justify-between group transition-all hover:shadow-sky-200/50">
                        <div className="space-y-4">
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <Star key={s} size={14} className={s <= review.rating ? "fill-amber-400 text-amber-400" : "text-gray-200"} />
                              ))}
                            </div>
                            <span className="text-[10px] font-black text-gray-300 uppercase tracking-widest">{new Date(review.createdAt).toLocaleDateString()}</span>
                          </div>

                          <div>
                            <h4 className="text-xl font-black text-sky-950 flex items-center gap-2">
                              <MapPin size={16} className="text-sky-400" />
                              {review.destination}
                            </h4>
                          </div>

                          <div className="bg-gray-50/50 p-4 rounded-2xl italic text-gray-600 text-sm leading-relaxed relative">
                            <MessageSquare size={14} className="absolute -top-2 -left-2 text-sky-100" />
                            "{review.comment}"
                          </div>
                        </div>

                        <div className="mt-6 pt-4 border-t border-gray-50 flex justify-end gap-3">
                          <button
                            onClick={() => handleEditReviewClick(review)}
                            className="p-3 bg-sky-50 text-sky-500 rounded-2xl hover:bg-sky-500 hover:text-white transition-all shadow-sm flex items-center gap-2 text-xs font-bold"
                          >
                            <Edit size={16} />
                            Edit Review
                          </button>
                          <button
                            onClick={() => handleDeleteClick(review)}
                            className="p-3 bg-red-50 text-red-500 rounded-2xl hover:bg-red-500 hover:text-white transition-all shadow-sm flex items-center gap-2 text-xs font-bold"
                          >
                            <Trash2 size={16} />
                            Remove Review
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-[2.5rem] p-12 text-center border-2 border-dashed border-sky-100">
                    <MessageSquare className="mx-auto text-sky-100 mb-6" size={80} />
                    <h3 className="text-2xl font-black text-sky-950">No Reviews Yet</h3>
                    <p className="text-gray-400 mt-2 font-medium max-w-xs mx-auto">Your travel stories are waiting to be told. Share your experiences with the world!</p>
                    <button onClick={() => navigate('/viewreview')} className="mt-6 px-8 py-4 bg-sky-500 text-white rounded-2xl font-black text-sm shadow-xl shadow-sky-500/30 hover:bg-sky-600 transition-all">Add My First Review</button>
                  </div>
                )}
              </div>
            )}

            {activeTab === "settings" && (
              <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
                <div className="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/40 border border-gray-100 overflow-hidden">
                  <div className="p-1">
                    <div className="bg-sky-50/30 rounded-[2.3rem] p-10">
                      <h2 className="text-3xl font-black text-sky-950 mb-2">Profile Intel</h2>
                      <p className="text-sky-600/60 font-medium mb-10">Manage your credentials and identification details.</p>

                      <form onSubmit={handleProfileUpdate} className="space-y-10">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          <div className="space-y-3">
                            <label className="text-xs font-black text-sky-900/40 uppercase tracking-widest ml-1 flex items-center gap-2">
                              <User size={12} /> Full Character Name
                            </label>
                            <input
                              type="text"
                              value={form.name}
                              onChange={(e) => setForm({ ...form, name: e.target.value })}
                              className="w-full px-6 py-4 bg-white border border-sky-100 rounded-[1.5rem] focus:outline-none focus:ring-4 focus:ring-sky-500/10 focus:border-sky-400 transition-all font-bold text-sky-950"
                              required
                            />
                          </div>
                          <div className="space-y-3 opacity-60 pointer-events-none">
                            <label className="text-xs font-black text-sky-900/40 uppercase tracking-widest ml-1 flex items-center gap-2">
                              <Mail size={12} /> Email (Verified)
                            </label>
                            <input
                              type="email"
                              value={form.email}
                              className="w-full px-6 py-4 bg-gray-100/50 border border-transparent rounded-[1.5rem] font-bold text-gray-500"
                              disabled
                            />
                          </div>
                          <div className="space-y-3">
                            <label className="text-xs font-black text-sky-900/40 uppercase tracking-widest ml-1 flex items-center gap-2">
                              <Phone size={12} /> Mobile Number
                            </label>
                            <input
                              type="text"
                              value={form.phone}
                              placeholder="+94 7X XXX XXXX"
                              onChange={(e) => setForm({ ...form, phone: e.target.value })}
                              className="w-full px-6 py-4 bg-white border border-sky-100 rounded-[1.5rem] focus:outline-none focus:ring-4 focus:ring-sky-500/10 focus:border-sky-400 transition-all font-bold text-sky-950"
                            />
                          </div>
                          <div className="space-y-3">
                            <label className="text-xs font-black text-sky-900/40 uppercase tracking-widest ml-1 flex items-center gap-2">
                              <MapPin size={12} /> Base Address
                            </label>
                            <input
                              type="text"
                              value={form.address}
                              placeholder="City, Country"
                              onChange={(e) => setForm({ ...form, address: e.target.value })}
                              className="w-full px-6 py-4 bg-white border border-sky-100 rounded-[1.5rem] focus:outline-none focus:ring-4 focus:ring-sky-500/10 focus:border-sky-400 transition-all font-bold text-sky-950"
                            />
                          </div>
                        </div>

                        {/* Security Section */}
                        <div className="pt-8 border-t border-sky-100/50">
                          <h4 className="text-sm font-black text-sky-950 uppercase tracking-widest mb-6 flex items-center gap-2">
                            <Lock size={16} className="text-sky-500" /> Security Updates
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-3 relative">
                              <label className="text-xs font-black text-sky-900/40 uppercase tracking-widest ml-1 flex items-center gap-2">
                                <Key size={12} /> Current Password
                              </label>
                              <div className="relative">
                                <input
                                  type={showCurrentPassword ? "text" : "password"}
                                  value={form.currentPassword}
                                  placeholder="Type to confirm change"
                                  onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
                                  className="w-full px-6 py-4 bg-white border border-sky-100 rounded-[1.5rem] focus:outline-none focus:ring-4 focus:ring-sky-500/10 focus:border-sky-400 transition-all font-bold text-sky-950"
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                  className="absolute right-5 top-1/2 -translate-y-1/2 text-sky-300 hover:text-sky-500 transition-colors"
                                >
                                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                              </div>
                            </div>
                            <div className="space-y-3 relative">
                              <label className="text-xs font-black text-sky-900/40 uppercase tracking-widest ml-1 flex items-center gap-2">
                                <Lock size={12} /> New Password
                              </label>
                              <div className="relative">
                                <input
                                  type={showNewPassword ? "text" : "password"}
                                  value={form.newPassword}
                                  placeholder="Leave blank to keep current"
                                  onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                                  className="w-full px-6 py-4 bg-white border border-sky-100 rounded-[1.5rem] focus:outline-none focus:ring-4 focus:ring-sky-500/10 focus:border-sky-400 transition-all font-bold text-sky-950"
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowNewPassword(!showNewPassword)}
                                  className="absolute right-5 top-1/2 -translate-y-1/2 text-sky-300 hover:text-sky-500 transition-colors"
                                >
                                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="pt-8 border-t border-sky-100 flex flex-col md:flex-row justify-between items-center gap-6">
                          <div className="text-sm text-sky-800/50 font-medium">
                            Last updated: <span className="font-bold text-sky-500">Real-time sync enabled</span>
                          </div>
                          <button
                            type="submit"
                            className="w-full md:w-auto px-10 py-4 bg-sky-500 text-white font-black rounded-2xl shadow-xl shadow-sky-500/30 hover:bg-sky-600 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
                          >
                            <Save size={20} />
                            Commit Changes
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>

                <div className="bg-rose-50 rounded-[2.5rem] p-10 flex flex-col md:flex-row items-center justify-between gap-8 border border-rose-100 shadow-xl shadow-rose-900/5">
                  <div className="space-y-2 text-center md:text-left">
                    <h3 className="text-xl font-black text-rose-950">Self-Destruct Sequence</h3>
                    <p className="text-sm text-rose-700/60 font-medium max-w-sm">Deleting your account is permanent. All your travel history and data will be erased forever.</p>
                  </div>
                  <button
                    onClick={handleDeleteAccount}
                    disabled={isDeleting}
                    className="px-8 py-4 bg-rose-500 text-white font-black rounded-2xl hover:bg-rose-600 shadow-xl shadow-rose-500/20 active:scale-95 transition-all text-sm flex items-center gap-2"
                  >
                    <Trash2 size={18} />
                    {isDeleting ? "Processing..." : "Terminate Account"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Fotter />

      {/* Reusable Confirm Modal from Admin for Logout */}
      <ConfirmModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={() => {
          logout();
          navigate("/");
          toast.info("Signed out successfully");
        }}
        title="Signing Out?"
        message="Are you sure you want to end your journey today? Your progress will be saved for next time."
      />

      <ConfirmModal
        isOpen={isReviewDeleteModalOpen}
        onClose={() => setIsReviewDeleteModalOpen(false)}
        onConfirm={confirmDeleteReview}
        title="Delete Review"
        message="Are you sure you want to delete this review? This action cannot be undone."
        confirmText="Delete"
      />

      <EditReviewModal
        isOpen={isReviewEditModalOpen}
        onClose={() => setIsReviewEditModalOpen(false)}
        review={selectedReview}
        onUpdate={fetchUserReviews}
      />
    </div>
  );
}

export default UserDashboard;
